/**
 * SKILLCARDS payantes (migration 0304) — accès client.
 *
 * Prix et accès viennent TOUJOURS de la base (`get_skillcard_offers`,
 * `get_skillcard_access`) ; le checkout (`create-skillcard-checkout`) relit le
 * devis côté serveur. Aucun montant n'est fourni par le client.
 */
import { useQuery } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import type {
  SkillcardAccess,
  SkillcardOfferKind,
  SkillcardOffers,
  SkillcardPriceGrid,
  SkillcardQuote,
} from '@jeraisonne/db/cards';
import { supabase } from '@/lib/supabase';
import {
  currentSkillcardStore,
  purchaseSkillcardInStore,
  storeQuote,
  usesStoreBilling,
} from './iap';
import { markCheckoutStarted } from '@/lib/analytics/track';

// RPC / tables postérieures aux types générés (stale volontaire).
const db = supabase as unknown as SupabaseClient;

export const skillcardOffersKey = (cardId: string, userId: string | null) =>
  ['skillcard-offers', cardId, userId ?? 'anon'] as const;
export const skillcardAccessKey = (cardId: string, userId: string | null) =>
  ['skillcard-access', cardId, userId ?? 'anon'] as const;

export function useSkillcardOffers(cardId: string, userId: string | null) {
  return useQuery({
    queryKey: skillcardOffersKey(cardId, userId),
    staleTime: 30_000,
    queryFn: async (): Promise<SkillcardOffers | null> => {
      const { data, error } = await db.rpc('get_skillcard_offers', { p_card_id: cardId });
      if (error) throw new Error(error.message);
      const offers = (data as SkillcardOffers | null) ?? null;
      if (!offers || !usesStoreBilling()) return offers;
      // App native : prix du palier du store (migration 0351).
      return {
        ...offers,
        card: storeQuote(offers.card),
        packs: offers.packs.map((p) => storeQuote(p)),
        programmes: offers.programmes.map((p) => storeQuote(p)),
      };
    },
  });
}

export function useSkillcardAccess(cardId: string, userId: string | null) {
  return useQuery({
    queryKey: skillcardAccessKey(cardId, userId),
    staleTime: 30_000,
    queryFn: async (): Promise<SkillcardAccess> => {
      const { data, error } = await db.rpc('get_skillcard_access', { p_card_id: cardId });
      if (error) throw new Error(error.message);
      return data as SkillcardAccess;
    },
  });
}

export function useSkillcardPriceGrid(enabled = true) {
  return useQuery({
    queryKey: ['skillcard-price-grid'],
    enabled,
    queryFn: async (): Promise<SkillcardPriceGrid> => {
      const { data, error } = await db.rpc('get_skillcard_price_grid');
      if (error) throw new Error(error.message);
      return data as SkillcardPriceGrid;
    },
  });
}

/** Date de fin du deck de l'élève connecté (RLS own_select). */
export function useMyDeck(userId: string | null | undefined) {
  return useQuery({
    queryKey: ['skillcard-deck', userId],
    enabled: !!userId,
    staleTime: 60_000,
    queryFn: async (): Promise<string | null> => {
      const { data, error } = await db
        .from('skillcard_decks')
        .select('expires_at')
        .eq('user_id', userId!)
        .maybeSingle();
      if (error) throw new Error(error.message);
      return (data as { expires_at: string } | null)?.expires_at ?? null;
    },
  });
}

export type SkillcardPurchaseStatus = 'pending' | 'paid' | 'failed';

/**
 * Suit un achat après le retour de Stripe : le webhook peut arriver quelques
 * secondes après la redirection. Sonde toutes les 2 s tant que `pending`.
 */
export function useSkillcardPurchaseStatus(purchaseId: string | null) {
  return useQuery({
    queryKey: ['skillcard-purchase', purchaseId],
    enabled: !!purchaseId,
    refetchInterval: (q) => (q.state.data === 'pending' ? 2000 : false),
    queryFn: async (): Promise<SkillcardPurchaseStatus | null> => {
      const { data, error } = await db
        .from('skillcard_purchases')
        .select('status')
        .eq('id', purchaseId!)
        .maybeSingle();
      if (error) throw new Error(error.message);
      return (data as { status: SkillcardPurchaseStatus } | null)?.status ?? null;
    },
  });
}

/**
 * Lance le paiement et renvoie l'URL où aller ensuite :
 *   - web : l'URL Stripe Checkout ;
 *   - app native : achat Google Play / App Store (palier de prix, 0351) PUIS le
 *     chemin interne de la page d'achat qui attend la confirmation.
 * Cadeau et achat pour un enfant restent sur le web.
 */
export async function startSkillcardCheckout(params: {
  kind: SkillcardOfferKind;
  targetId: string;
  cardId: string;
  returnPath?: string | null;
  /** Parent qui achète pour son enfant lié (migration 0326). */
  forStudentId?: string | null;
  /** Carte cadeau : un code est créé au paiement (migration 0326). */
  gift?: boolean;
  /** Achat depuis la page d'un pack : Stripe revient sur /pack/{id}/acheter (0337). */
  fromPackId?: string | null;
}): Promise<string> {
  markCheckoutStarted('skillcards');
  if (usesStoreBilling()) {
    if (params.gift || params.forStudentId) throw new Error('web_only');
    return purchaseSkillcardInStore(params);
  }
  const { data, error } = await supabase.functions.invoke('create-skillcard-checkout', {
    body: {
      kind: params.kind,
      target_id: params.targetId,
      card_id: params.cardId,
      return_path: params.returnPath ?? null,
      for_student_id: params.forStudentId ?? null,
      gift: params.gift === true,
      from_pack_id: params.fromPackId ?? null,
    },
  });
  const code = (data as { error?: string } | null)?.error;
  if (error || !(data as { url?: string } | null)?.url) {
    throw new Error(code ?? (await errorCodeFromInvoke(error)) ?? 'unknown');
  }
  return (data as { url: string }).url;
}

async function errorCodeFromInvoke(error: unknown): Promise<string | null> {
  const ctx = (error as { context?: Response } | null)?.context;
  if (ctx && typeof ctx.json === 'function') {
    try {
      const body = (await ctx.json()) as { error?: string };
      return body.error ?? null;
    } catch {
      return null;
    }
  }
  return null;
}

export function skillcardCheckoutErrorMessage(code: string): string {
  switch (code) {
    case 'already_owned':
      return 'Tu as déjà toutes ces cartes dans ton deck.';
    case 'not_for_sale':
      return 'Cette offre n’est plus disponible.';
    case 'account_required':
    case 'unauthenticated':
      return 'Connecte-toi avec ton compte pour acheter.';
    case 'not_your_child':
      return 'Ce compte n’est pas relié au vôtre comme enfant.';
    case 'user_cancelled':
      return 'Achat annulé.';
    case 'web_only':
      return 'Cet achat se fait sur jeraisonne.fr.';
    case 'no_store_tier':
      return 'Cette offre est trop chère pour un achat dans l’app : passe par jeraisonne.fr.';
    case 'store_unavailable':
    case 'store_product_missing':
      return currentSkillcardStore() === 'app_store'
        ? 'L’App Store n’est pas disponible pour le moment. Réessaie dans un instant.'
        : 'Google Play n’est pas disponible pour le moment. Réessaie dans un instant.';
    case 'store_failed':
      return currentSkillcardStore() === 'app_store'
        ? 'Le paiement via l’App Store n’a pas abouti.'
        : 'Le paiement Google Play n’a pas abouti.';
    default:
      return 'Le paiement n’a pas pu démarrer. Réessaie dans un instant.';
  }
}

/** Chemin de la page d'achat d'une carte (retour éventuel vers l'exercice). */
export function skillcardBuyPath(cardId: string, returnPath?: string | null): string {
  const base = `/card/${cardId}/acheter`;
  return returnPath ? `${base}?return=${encodeURIComponent(returnPath)}` : base;
}

/** Chemin de la page d'achat d'un pack (retour éventuel). */
export function packBuyPath(packId: string, returnPath?: string | null): string {
  const base = `/pack/${packId}/acheter`;
  return returnPath ? `${base}?return=${encodeURIComponent(returnPath)}` : base;
}

/** Ne garde qu'un chemin interne (pas d'hôte, pas de `//`) — anti open redirect. */
export function safeInternalPath(path: unknown): string | null {
  if (typeof path !== 'string' || path.length > 500) return null;
  if (!path.startsWith('/') || path.startsWith('//') || path.includes('\\')) return null;
  return path;
}

/** « 1 an » pour 12 mois, « 2 ans » pour 24, sinon « N mois ». */
export function validityLabel(months: number): string {
  if (months > 0 && months % 12 === 0) {
    const years = months / 12;
    return years === 1 ? '1 an' : `${years} ans`;
  }
  return `${months} mois`;
}

// ── Achat d'un PACK (/pack/$packId/acheter, migration 0337) ─────────────────

export interface PackOffersCard {
  id: string;
  title: string;
  color_theme: 'level' | 'night' | null;
  creature: unknown;
  levels: string[];
  rarity: string;
}

export interface PackOffers {
  pack: {
    id: string;
    title: string;
    hook: string;
    level: string;
    subject: string;
    color: string | null;
    icon: string | null;
    start_month: number;
    end_month: number | null;
  };
  cards: PackOffersCard[];
  pack_offer: SkillcardQuote | null;
  programme_offer: SkillcardQuote | null;
  /** Accès au test du pack (0336) : admin / subscription / pass / owned, sinon null. */
  access: string | null;
  settings: { unit_price_cents: number; validity_months: number };
}

export function usePackOffers(packId: string, userId: string | null) {
  return useQuery({
    queryKey: ['skillcard-offers', 'pack', packId, userId ?? 'anon'],
    staleTime: 30_000,
    queryFn: async (): Promise<PackOffers | null> => {
      const { data, error } = await db.rpc('get_pack_offers', { p_pack_id: packId });
      if (error) throw new Error(error.message);
      const offers = (data as PackOffers | null) ?? null;
      if (!offers || !usesStoreBilling()) return offers;
      return {
        ...offers,
        pack_offer: offers.pack_offer && storeQuote(offers.pack_offer),
        programme_offer: offers.programme_offer && storeQuote(offers.programme_offer),
      };
    },
  });
}

/** Message de déblocage du pack : null si rien n'est encore débloqué. */
export function packUnlockedLabel(access: string | null | undefined): string | null {
  switch (access) {
    case 'owned':
      return 'Ce pack est déjà débloqué : toutes ses SKILLCARDS sont dans ton deck';
    case 'subscription':
      return 'Ton abonnement inclut déjà ce pack';
    case 'pass':
      return 'Ton accès en cours inclut déjà ce pack';
    case 'admin':
      return 'Accès administrateur';
    default:
      return null;
  }
}
